<template>
  <div
    :data-ab-id="dataAbId"
  >
    <KikSkeletonProductCard
      v-if="loading"
      class="mb-4"
      :number="$device.isMobile ? 2 : 6"
      height="430px"
    />
    <template v-else>
      <template v-if="shouldDisplayCarousel">
        <KikHeading
          v-if="isRecommendation"
          :level="recommendationType === 'homepage_personalized' ? 2 : 3"
          :class="recommendationType === 'homepage_personalized' ? 'text-left' : 'text-center'"
          :title="$t(recommendationTitle)"
        />
        <KikCarousel class="!gap-0">
          <KikProductCard
            v-for="product in products"
            :key="product.id"
            :product="product"
            :image-load-type="imageLoadType"
            :is-slider="true"
            class="max-w-[calc(60%-8px)] flex-[0_0_60%] !border-b first:border-l"
          />
        </KikCarousel>
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { useTeaserSlider } from '~/components/Shared/Teaser/Slider/useTeaserSlider/useTeaserSlider';
import { useMagnolia } from '~/composables/custom/useMagnolia/useMagnolia';
import { useTagsLoader } from '~/composables/custom/useTagLoader/useTagLoader';
import { useTracking } from '~/composables/custom/useTracking';
import { useSSR } from '~/composables/useSSR/useSSR';
import type { Category } from '~/types/GraphQL';

type RecommendationType = 'product_detail' | 'product_detail_complements' | 'homepage_personalized';

interface Props {
  productCategories: string[];
  productCTATargetMode?: string;
  imageLoadType?: HTMLImageElement['loading'];
  isRecommendation?: boolean;
  recommendationType?: RecommendationType;
  productId?: string | null | undefined;
  operator?: string;
  byAttributes?: any;
  fallbackCategory?: any;
  dataAbId?: string;
}

const titleMap: Record<RecommendationType, string> = {
  /* eslint-disable camelcase */
  homepage_personalized: 'Recommended for you',
  product_detail: 'Similar products',
  product_detail_complements: 'More recommendations for you',
  /* eslint-enable camelcase */
};

const props = withDefaults(defineProps<Props>(), {
  productCTATargetMode: '_self',
  imageLoadType: 'lazy',
  isRecommendation: false,
  recommendationType: 'homepage_personalized',
  productId: null,
});

const productCategories = toRef(props, 'productCategories');

const { loading, products, fetchProducts, fetchRecommendations } = useTeaserSlider(props.isRecommendation ? [`recco-${props.recommendationType}-${props.productId}`] : productCategories.value);

const { fetchTags } = useTagsLoader();

const { atMagnolia } = useMagnolia();
const { trackViewSlider } = useTracking();
const { onSSR } = useSSR();

const shouldDisplayCarousel = computed(() => !loading.value && products.value.length > 4);
const recommendationTitle = computed(() => titleMap[props.recommendationType] ?? 'More recommendations for you');

onSSR(async () => {
  if (!atMagnolia.value) {
    return;
  }

  if (!products.value?.length) {
    if (props.isRecommendation) {
      await fetchRecommendations(props.recommendationType, props.productId, props.fallbackCategory);
    }
    else {
      await fetchProducts(props.isRecommendation ? [`recco-${props.recommendationType}-${props.productId}`] : productCategories.value, props.operator, props.byAttributes);
    }
    await fetchTags();
  }
});

watch(products, () => {
  if (products.value?.length) {
    if (props.isRecommendation) {
      trackViewSlider({ id: props.productId, name: `Recommendations-${props.recommendationType}` } as Category, products.value);
    }
    else {
      trackViewSlider({ id: props.productId, name: 'TeaserSlider' } as Category, products.value);
    }
  }
});

onBeforeMount(async () => {
  if (!products.value?.length) {
    if (props.isRecommendation) {
      await fetchRecommendations(props.recommendationType, props.productId, props.fallbackCategory);
    }
    else {
      await fetchProducts(props.isRecommendation ? [`recco-${props.recommendationType}-${props.productId}`] : productCategories.value, props.operator, props.byAttributes);
    }
  }
});
</script>
